/**
 * **そのフレームの duelEditor の舞台の層**（Issue #523 / #638）。**`duelEditor/timeline/` の外**。
 *
 * 盤・オブジェクト・HUD は**型（タイムラインの帯）ではない**ので毎フレーム描き、
 * 帯を持つのは演出の層だけ（`frame.items`）。
 *
 * ## これで何が繋がるか
 *
 * - 再生位置は **editor のコアが持つ**（`editor.transport.ms`）。`ClockPort`（`videoClock.js`）が
 *   実時計を一方向に押し込む
 * - 盤面・LP・ターン・フェイズは**状態チャンネル**（`ocgSource.js`）から来る。
 *   `duelEditor/` は時間の仕組みを 1 つも持たない（受け取るのは「そのフレームの値」だけ）
 * - **テーマ**（`duelEditor/themes/`）は `env.config.theme` で選ぶ
 *
 * 使うのは `pages/useDuelStage.js`（`/duel-timing`・`/duel-editor`）。
 */
import BackgroundLayer, { backgroundOf } from '../duelEditor/stage/BackgroundLayer';
import FieldLayer from '../duelEditor/stage/FieldLayer';
import EffectLayer, {
  effectHud, effectTweaks, mergeTweaks, sceneSplit,
} from '../duelEditor/stage/EffectLayer';
import ObjectLayer from '../duelEditor/stage/ObjectLayer';
import HudLayer from '../duelEditor/stage/HudLayer';
import EditLayer, { editBoxes } from '../duelEditor/stage/EditLayer';
import { hudValues } from '../duelEditor/core/hud';
import { resolveStyle } from '../duelEditor/core/theme';
import { DEFAULT_THEME } from '../duelEditor/themes';
import { toDuelScene } from './duelScene';
import { boardFilter } from './fxScenePicture';

/** 返す層の名前（`duelEditor/canvas/layers.js` の宣言と同じ綴り。この順に重なる） */
export const DUEL_STAGE_SLOTS = Object.freeze(['background', 'field', 'objects', 'effects', 'hud', 'front', 'edit']);

/**
 * そのフレームの舞台の層。**名前つき**で返す（`duelEditor/canvas/Canvas.js` がこの形を取る）。
 *
 * @param frame `editor.frameAt(ms)` の結果
 * @param env `{ config }`。config は描画側の語彙（コアは素通しする）
 */
export function duelStageLayers(frame, env = {}) {
  const config = env?.config ?? {};
  const { view } = config;
  // カメラが無いと投影できない（読み込み中）
  if (!view) return [];
  const theme = config.theme ?? DEFAULT_THEME;
  const scene = toDuelScene(frame, config);
  /*
   * **演出が盤のカードを動かす**（R13。Issue #541）。ページの「試しに動かす」と重ねて渡す
   * （演出が後 ＝ 手で動かした上に演出の動きが乗る）。
   */
  const tweaks = mergeTweaks(
    config.objectTweaks ?? null,
    effectTweaks(theme, scene, view, config.canvas, frame?.items ?? [], config.stage),
  );
  /*
   * **画面を切ってずらす**（Issue #553）。盤・オブジェクト・演出の 3 つの three の層に**同じ値**を渡す
   * （層をまたいで同じ線で切れる）。HUD は盤の外の表示なので切らない。
   */
  const split = sceneSplit(theme, scene, view, config.canvas, config.stage);
  const style = resolveStyle(theme, config.stage?.style);
  /*
   * **舞台の背景**（Issue #565）。テーマが持たなければ出さない（器の色のまま）。
   * 画面を切る（`split`）のは盤から上の three の層だけで、背景は切らない（舞台の外の景色）。
   */
  const bg = backgroundOf(theme, style);
  /*
   * **盤の側の層だけの色の調整**（`scene.grade.boardGrayscale`。Issue #604）。背景・盤・置かれたカードの層に CSS の filter を
   * 当てる（HUD と演出の層は色のまま）。舞台全体の filter（`gradeFilter`）とは別
   */
  const bf = boardFilter(scene?.fxScene?.grade ?? null) || undefined;
  const out = [
    ...(bg ? [{ layer: 'background', key: 'background', filter: bf, node: <BackgroundLayer key="background" bg={bg} canvas={config.canvas} style={style} /> }] : []),
    { layer: 'field', key: 'field', filter: bf, node: <FieldLayer key="field" theme={theme} scene={scene} view={view} config={config.stage} split={split} /> },
    /*
     * **`tweaks` は設定（`config.stage`）に混ぜない**（Issue #523）—— 毎フレーム変わるので、
     * 混ぜると `FieldLayer` の `layerDeps` が毎フレーム変わって**盤の紙まで描き直し**になる。
     */
    { layer: 'objects', key: 'objects', filter: bf, node: <ObjectLayer key="objects" theme={theme} scene={scene} view={view} canvas={config.canvas} config={config.stage} tweaks={tweaks} split={split} /> },
    /*
     * **演出の層**（Issue #529）。**帯を受け取るのはここだけ** —— 盤・オブジェクト・HUD は
     * 型ではないので、演出だけが `frame.items` から来る。
     */
    { layer: 'effects', key: 'effects', node: <EffectLayer key="effects" theme={theme} scene={scene} view={view} canvas={config.canvas} clips={frame?.items ?? []} config={config.stage} split={split} /> },
    {
      layer: 'hud',
      key: 'hud',
      // **演出が HUD に当てる作用**（Issue #542。LP の枠が揺れる・光る）
      node: <HudLayer key="hud" theme={theme} scene={scene} view={view} canvas={config.canvas} config={config.stage} fx={effectHud(theme, scene, view, config.canvas, frame?.items ?? [], config.stage)} />,
    },
    /*
     * **HUD より手前の演出**（層の `front: true`。飛び出すカードなど）。同じ帯を読んで手前の側の層だけを描く。
     * HUD は切らないのでこちらも `split` を渡さない。舞台は常駐（帯のたびに WebGL のコンテキストを作らない）
     */
    { layer: 'front', key: 'front', node: <EffectLayer key="front" slot="front" theme={theme} scene={scene} view={view} canvas={config.canvas} clips={frame?.items ?? []} config={config.stage} /> },
  ];
  /*
   * **編集の層**（Issue #523）。テーマを詰めるときだけ出す（**書き出しには出さない**）。
   * 押せるものは器が既に持っているものから出すので、テーマに口を足していない。
   */
  if (config.editing) {
    const boxes = editBoxes({
      theme, scene, view, canvas: config.canvas,
      options: { ...theme.hud.options, ...(config.stage?.hud ?? {}) },
      style,
      hudValues: hudValues(scene.hud),
    });
    out.push({
      layer: 'edit',
      key: 'edit',
      node: <EditLayer key="edit" boxes={boxes} selected={config.selected} scale={config.scale} onSelect={config.onSelect} />,
    });
  }
  return out;
}
